import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { AhsTranslationService } from '@nx-ahs/base-translate';

import { PortalLanguage } from './shared/languages/portalLanguage';
import { PortalPageName } from './shared/languages/registeredPortalPageNames';
import { AppCoreService } from './core/services/app-core.service';

@Injectable({
	providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler, PortalLanguage {
	public readonly DB_PAGE_NAME: PortalPageName = 'error-handler';
	private readonly ALERT_COUNT_KEY = 'globalErrorCount';
	private readonly ALERT_SHOWN_KEY = 'errorAlertShown';

	constructor(
		private appCoreService: AppCoreService,
		private translateService: AhsTranslationService
	) {}

	public handleError(error: any): void {
		// Ignore 426 (Upgrade Required) errors - these are handled by AppVersionService
		if (error instanceof HttpErrorResponse && error.status === 426) {
			return;
		}

		// Log the detailed error to the browser console.
		console.error('GlobalErrorHandler:', error);

		// If the alert flag is already set, do nothing.
		if (localStorage.getItem(this.ALERT_SHOWN_KEY)) {
			return;
		}

		// Retrieve max error count from the client config.
		const maxErrors = this.appCoreService.clientConfig?.errorAlertConfig?.maxErrorAlertCount;

		if (maxErrors) {
			const currentCount = this.getErrorCount();

			// Only trigger the alert if the error count is below the maximum threshold.
			if (currentCount >= maxErrors) {
				return;
			}

			this.setErrorCount(currentCount + 1);

			// Get the fallback text from configuration (or fall back to the translated text)
			const alertText =
				this.appCoreService.clientConfig?.errorAlertConfig?.fallbackErrorText ||
				this.translateService.translate.instant('GLOBAL_ERROR_MESSAGE');

			const refresh = confirm(alertText);

			// Set the flag so that further errors don't trigger the alert again until navigation.
			localStorage.setItem(this.ALERT_SHOWN_KEY, 'true');

			// Reset the alert flag after 30 seconds (this is non-blocking)
			setTimeout(() => {
				localStorage.removeItem(this.ALERT_SHOWN_KEY);
			}, 30000);

			// If the user pressed OK, then refresh the page.
			if (refresh) {
				window.location.reload();
			}
		} else {
			return;
		}
	}

	/**
	 * Retrieve the current error count from local storage.
	 */
	private getErrorCount(): number {
		const storedCount = localStorage.getItem(this.ALERT_COUNT_KEY);
		const count = storedCount ? parseInt(storedCount, 10) : 0;
		return isNaN(count) ? 0 : count;
	}

	/**
	 * Persist the error count to local storage.
	 */
	private setErrorCount(count: number): void {
		localStorage.setItem(this.ALERT_COUNT_KEY, count.toString());
	}
}
